/**
 * InfiniumLogo.tsx
 * ─────────────────
 * SVG brand components for Infinium Robotics.
 *
 * InfiniumWordmark  — full stacked logo (infinity mark + "INFINIUM" + "ROBOTICS")
 *                     use on the login card (variant="light") and splash screens.
 *
 * InfiniumMark      — compact horizontal logo (small infinity + wordmark inline)
 *                     use in app headers.
 */

interface WordmarkProps {
  /** "light" = dark text for white/light surfaces; "dark" = white text for dark surfaces */
  variant?: 'light' | 'dark';
  /** Rendered height in px (width scales proportionally). Default 120. */
  height?: number;
}

/** Full stacked wordmark: infinity symbol above, INFINIUM + ROBOTICS below. */
export function InfiniumWordmark({ variant = 'light', height = 120 }: WordmarkProps) {
  const teal      = '#3ECECE';
  const textColor = variant === 'light' ? '#2a2a2a' : '#ffffff';
  // viewBox: 200 wide × 135 tall
  const width = (200 / 135) * height;

  return (
    <svg
      viewBox="0 0 200 135"
      width={width}
      height={height}
      aria-label="Infinium Robotics"
      role="img"
    >
      {/* ── Infinity / lemniscate ─────────────────────────────────────────── */}
      {/*
          Stroke-based figure-8: starts at center crossing (100,40),
          traces right loop clockwise, then left loop clockwise.
          strokeLinecap="round" gives the rounded tube appearance.
      */}
      <path
        d="M 100,40
           C 100,14 138,4 160,18
           C 182,32 182,52 160,62
           C 138,72 104,56 100,40
           C 96,24 62,8 40,18
           C 18,28 18,52 40,62
           C 62,72 96,56 100,40"
        fill="none"
        stroke={teal}
        strokeWidth="17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* ── INFINIUM ─────────────────────────────────────────────────────── */}
      <text
        x="100"
        y="102"
        textAnchor="middle"
        fontFamily="'Arial Black', 'Helvetica Neue', Arial, sans-serif"
        fontSize="26"
        fontWeight="900"
        fill={textColor}
        letterSpacing="1"
      >
        INFINIUM
      </text>

      {/* ── ROBOTICS ─────────────────────────────────────────────────────── */}
      <text
        x="100"
        y="121"
        textAnchor="middle"
        fontFamily="Arial, 'Helvetica Neue', sans-serif"
        fontSize="12"
        fontWeight="600"
        fill={textColor}
        letterSpacing="5"
      >
        ROBOTICS
      </text>
    </svg>
  );
}

interface MarkProps {
  variant?: 'light' | 'dark';
  /** Rendered height in px. Default 36. */
  height?: number;
}

/** Compact horizontal mark: small infinity icon + "INFINIUM ROBOTICS" text inline. */
export function InfiniumMark({ variant = 'dark', height = 36 }: MarkProps) {
  const teal      = '#3ECECE';
  const textColor = variant === 'light' ? '#2a2a2a' : '#ffffff';
  // viewBox: 210 wide × 44 tall
  const width = (210 / 44) * height;

  return (
    <svg
      viewBox="0 0 210 44"
      width={width}
      height={height}
      aria-label="Infinium Robotics"
      role="img"
    >
      {/* ── Compact infinity mark ─────────────────────────────────────────── */}
      <path
        d="M 34,22
           C 34,10 46,5 54,10
           C 62,15 67,22 74,22
           C 81,22 86,15 94,10
           C 102,5 114,10 114,22
           C 114,34 102,39 94,34
           C 86,29 81,22 74,22
           C 67,22 62,29 54,34
           C 46,39 34,34 34,22"
        fill="none"
        stroke={teal}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* ── INFINIUM ─────────────────────────────────────────────────────── */}
      <text
        x="125"
        y="20"
        fontFamily="'Arial Black', 'Helvetica Neue', Arial, sans-serif"
        fontSize="14"
        fontWeight="900"
        fill={textColor}
        letterSpacing="0.5"
      >
        INFINIUM
      </text>

      {/* ── ROBOTICS ─────────────────────────────────────────────────────── */}
      <text
        x="126"
        y="34"
        fontFamily="Arial, 'Helvetica Neue', sans-serif"
        fontSize="8"
        fontWeight="600"
        fill={textColor}
        letterSpacing="3"
      >
        ROBOTICS
      </text>
    </svg>
  );
}
